// The email body as a short list of blocks: a heading, some text, a button, an image, a quote, a signature.
// Each block edits in place; the live preview beside it shows exactly what people get. Used by emails and by
// each email in a sequence.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { BLOCK_TYPES, MERGE_TAGS, type Block, type BlockType } from "@couragemfg/api/email-render";
import { uid } from "./emApi";

const LABEL: Record<BlockType, string> = Object.fromEntries(BLOCK_TYPES.map((b) => [b.type, b.label])) as Record<BlockType, string>;

export function newBlock(type: BlockType): Block {
  const id = uid();
  switch (type) {
    case "heading": return { id, type, text: "" };
    case "text": return { id, type, text: "" };
    case "button": return { id, type, label: "Learn more", url: "https://" };
    case "image": return { id, type, src: "", alt: "" };
    case "quote": return { id, type, text: "", by: "" };
    case "spacer": return { id, type };
    default: return { id, type } as Block;
  }
}

/** Insert text at the cursor of the field that was last focused. */
function useInsert() {
  const ref = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const insert = (value: string, current: string, set: (v: string) => void) => {
    const el = ref.current;
    const at = el && typeof el.selectionStart === "number" ? el.selectionStart : current.length;
    const end = el && typeof el.selectionEnd === "number" ? el.selectionEnd : at;
    set(current.slice(0, at) + value + current.slice(end));
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(at + value.length, at + value.length); } });
  };
  return { ref, insert };
}

export function MergeTagMenu({ onPick, label = "Insert" }: { onPick: (tag: string) => void; label?: string }) {
  return (
    <select className="select em-tagpick" aria-label="Insert a merge tag" value="" onChange={(e) => { if (e.target.value) onPick(e.target.value); }}>
      <option value="">{label}…</option>
      {MERGE_TAGS.map((t) => <option key={t.tag} value={t.example}>{t.label}</option>)}
    </select>
  );
}

/** `types`: the blocks that can be added (a landing page has no signature). */
export function BlockEditor({ blocks, onChange, disabled, types }: { blocks: Block[]; onChange: (b: Block[]) => void; disabled?: boolean; types?: BlockType[] }) {
  const [adding, setAdding] = useState<number | null>(null);
  const set = (i: number, b: Block) => onChange(blocks.map((x, j) => (j === i ? b : x)));
  const move = (i: number, d: -1 | 1) => { const j = i + d; if (j < 0 || j >= blocks.length) return; const n = [...blocks]; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  const remove = (i: number) => onChange(blocks.filter((_, j) => j !== i));
  const copy = (i: number) => onChange([...blocks.slice(0, i + 1), { ...blocks[i], id: uid() }, ...blocks.slice(i + 1)]);
  const add = (at: number, type: BlockType) => { onChange([...blocks.slice(0, at), newBlock(type), ...blocks.slice(at)]); setAdding(null); };
  return (
    <div className="em-blocks">
      {blocks.map((b, i) => (
        <div key={b.id}>
          <BlockCard block={b} index={i} count={blocks.length} disabled={disabled} onChange={(x) => set(i, x)} onMove={(d) => move(i, d)} onRemove={() => remove(i)} onCopy={() => copy(i)} />
          {!disabled && (adding === i + 1
            ? <AddMenu types={types} onPick={(t) => add(i + 1, t)} onClose={() => setAdding(null)} />
            : <button type="button" className="em-insert" onClick={() => setAdding(i + 1)} aria-label={`Add a block after block ${i + 1}`}><span>+</span></button>)}
        </div>
      ))}
      {!blocks.length && !disabled && <AddMenu types={types} onPick={(t) => add(0, t)} />}
    </div>
  );
}

function AddMenu({ onPick, onClose, types }: { onPick: (t: BlockType) => void; onClose?: () => void; types?: BlockType[] }) {
  return (
    <div className="em-add" role="group" aria-label="Add a block">
      {BLOCK_TYPES.filter((t) => !types || types.includes(t.type)).map((t) => <button type="button" key={t.type} onClick={() => onPick(t.type)} title={t.hint}>{t.label}</button>)}
      {onClose && <button type="button" className="x" onClick={onClose} aria-label="Close">×</button>}
    </div>
  );
}

function Tool({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return <button type="button" className="em-tool" aria-label={label} title={label} onClick={onClick} disabled={disabled}>{children}</button>;
}

function BlockCard({ block: b, index, count, disabled, onChange, onMove, onRemove, onCopy }: {
  block: Block; index: number; count: number; disabled?: boolean; onChange: (b: Block) => void; onMove: (d: -1 | 1) => void; onRemove: () => void; onCopy: () => void;
}) {
  const txt = useInsert();
  const f = (k: string) => (e: { target: { value: string } }) => onChange({ ...b, [k]: e.target.value } as Block);
  const id = (k: string) => `blk-${b.id}-${k}`;
  return (
    <div className={`em-block em-block-${b.type}`}>
      <div className="em-block-h">
        <span className="t">{LABEL[b.type]}</span>
        {!disabled && <span className="tools">
          <Tool label="Move up" onClick={() => onMove(-1)} disabled={index === 0}><svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M6 2v8M2.5 5.5 6 2l3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" /></svg></Tool>
          <Tool label="Move down" onClick={() => onMove(1)} disabled={index === count - 1}><svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M6 10V2M2.5 6.5 6 10l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" /></svg></Tool>
          <Tool label="Duplicate" onClick={onCopy}><svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><rect x="3.5" y="3.5" width="7" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="1.2" /><path d="M1.5 8V2.5a1 1 0 0 1 1-1H8" fill="none" stroke="currentColor" strokeWidth="1.2" /></svg></Tool>
          <Tool label="Remove" onClick={onRemove}><svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="currentColor" strokeWidth="1.4" /></svg></Tool>
        </span>}
      </div>
      <fieldset disabled={disabled} className="em-block-b">
        {b.type === "heading" && (
          <div className="em-row">
            <input id={id("t")} className="input em-heading-in" value={b.text} onChange={f("text")} placeholder="What this email is about" aria-label="Heading" ref={txt.ref as any} />
            <select className="select em-size" aria-label="Heading size" value={b.size ?? "large"} onChange={(e) => onChange({ ...b, size: e.target.value === "medium" ? "medium" : undefined })}><option value="large">Large</option><option value="medium">Medium</option></select>
          </div>)}
        {b.type === "text" && (<>
          <textarea id={id("t")} ref={txt.ref as any} className="textarea em-text-in" rows={Math.min(14, Math.max(3, b.text.split("\n").length + 1))} value={b.text} onChange={f("text")}
            placeholder={"Hi {{first_name|there}},\n\nWrite like you'd write to one customer."} aria-label="Text" />
          <div className="em-hint"><span>**bold** · *italic* · [link text](https://…) · start a line with "- " for a list</span>{!disabled && <MergeTagMenu onPick={(t) => txt.insert(t, b.text, (v) => onChange({ ...b, text: v }))} />}</div>
        </>)}
        {b.type === "button" && (
          <div className="em-grid3">
            <div className="field"><label htmlFor={id("l")}>Label</label><input id={id("l")} className="input" value={b.label} onChange={f("label")} placeholder="Book a call" /></div>
            <div className="field"><label htmlFor={id("u")}>Link</label><input id={id("u")} className="input" value={b.url} onChange={f("url")} placeholder="https://yourcompany.com/…" inputMode="url" /></div>
            <div className="field"><label htmlFor={id("a")}>Position</label><select id={id("a")} className="select" value={b.align ?? "center"} onChange={(e) => onChange({ ...b, align: e.target.value === "left" ? "left" : undefined })}><option value="center">Centered</option><option value="left">Left</option></select></div>
          </div>)}
        {b.type === "image" && (
          <div className="em-image">
            <div className="em-grid2">
              <div className="field"><label htmlFor={id("s")}>Image address</label><input id={id("s")} className="input" value={b.src} onChange={f("src")} placeholder="https://yourcompany.com/photo.jpg" inputMode="url" /><span className="hint">A public https:// link to a JPG, PNG or GIF</span></div>
              <div className="field"><label htmlFor={id("al")}>Describe it</label><input id={id("al")} className="input" value={b.alt ?? ""} onChange={f("alt")} placeholder="The new 5-axis cell" /><span className="hint">Shown when images are off, and read aloud</span></div>
              <div className="field"><label htmlFor={id("u")}>Opens (optional)</label><input id={id("u")} className="input" value={b.url ?? ""} onChange={f("url")} placeholder="https://…" inputMode="url" /></div>
              <div className="field"><label htmlFor={id("w")}>Width</label><select id={id("w")} className="select" value={b.width ?? "full"} onChange={(e) => onChange({ ...b, width: e.target.value === "half" ? "half" : undefined })}><option value="full">Full width</option><option value="half">Half width</option></select></div>
            </div>
            {/^https:\/\//i.test(b.src) && <img src={b.src} alt="" className="em-thumb" />}
          </div>)}
        {b.type === "quote" && (
          <div className="em-grid2">
            <div className="field" style={{ gridColumn: "1 / -1" }}><label htmlFor={id("q")}>Quote</label><textarea id={id("q")} className="textarea" rows={3} value={b.text} onChange={f("text")} placeholder="They quoted in a day and shipped first articles in two weeks." /></div>
            <div className="field"><label htmlFor={id("by")}>Who said it</label><input id={id("by")} className="input" value={b.by ?? ""} onChange={f("by")} placeholder="Dana Reyes, Quality Manager, Globex" /></div>
          </div>)}
        {b.type === "spacer" && (
          <select className="select" style={{ width: 160 }} aria-label="Space" value={b.size ?? "m"} onChange={(e) => onChange({ ...b, size: e.target.value === "m" ? undefined : (e.target.value as "s" | "l") })}><option value="s">Small</option><option value="m">Medium</option><option value="l">Large</option></select>)}
        {b.type === "divider" && <p className="muted em-note">A thin line between sections.</p>}
        {b.type === "signature" && <p className="muted em-note">The sender's name, your company and the reply-to address. With "From the account owner" on, it's each person's own account owner.</p>}
      </fieldset>
    </div>
  );
}

/** Width a desktop mail app gives an email; narrower previews are scaled down so a 600px design shows as it will on a desktop. */
const DESKTOP = 680;

/** The rendered email in a frame, at desktop or phone width. Sandboxed with no scripts: imported HTML can't run. */
export function EmailFrame({ html, device, title = "Email preview" }: { html: string; device: "desktop" | "phone"; title?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (device !== "desktop" || !el || typeof ResizeObserver === "undefined") { setBox(null); return; }
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [device]);
  const scale = box && box.w > 0 && box.w < DESKTOP ? box.w / DESKTOP : 1;
  return (
    <div ref={ref} className={`em-frame ${device}${scale < 1 ? " scaled" : ""}`}>
      <iframe title={title} srcDoc={html} sandbox="allow-popups allow-popups-to-escape-sandbox"
        style={scale < 1 && box ? { width: DESKTOP, height: box.h / scale, transform: `scale(${scale})` } : undefined} />
    </div>
  );
}
